import {
  type ArchiveEstimate,
  type BulkArchivePrepareResult,
  type BulkArchiveRequest,
  type PublicFolder,
  type ShareLink,
  type ShareTargetKind,
  archiveEstimateSchema,
  bulkArchivePrepareResultSchema,
  publicFolderSchema,
  shareFolderArchiveEstimatePath,
  shareFolderArchivePath,
  shareFolderArchivePreparePath,
  shareFolderFileLinkPath,
  shareFolderPath,
  shareLinkSchema,
} from '@xfile/shared';
import { apiFetch } from '../lib/http';
import { StorageApiError } from '../storage/storage-api';

/**
 * Client des LIENS DE PARTAGE (XFILE-05, -06 ; ADR-0005). Deux natures d'appel :
 *
 * - ÉCRITURE (`ensureShare`) : authentifiée, via `apiFetch` (cookie + `X-Active-Slot`),
 *   garantit (idempotent) un lien de partage pour une cible possédée. Erreurs
 *   normalisées en `StorageApiError` comme le reste du client de stockage.
 * - RÉSOLUTION PUBLIQUE (`fetchPublicFolder`) : NON authentifiée, via `fetch` NU (ces
 *   pages sont accessibles DÉCONNECTÉ — aucun slot/cookie requis). Le résultat est
 *   DISCRIMINÉ (`ok`/`gone`/`notFound`/`error`) : un slug inexistant → `404` → `notFound` ; une
 *   cible expirée/supprimée (TTL/GC/soft-delete) → `410 Gone` (`ErrorCode.GONE`) → `gone` ; et
 *   tout ce qui empêche d'obtenir un verdict (réseau, 5xx, corps illisible) → `error`, JAMAIS
 *   replié sur `notFound` (cf. le type ci-dessous). La fonction ne rejette donc pas.
 *   (Un FICHIER partagé `/f/:slug` est servi DIRECTEMENT par le serveur en octets bruts —
 *   intégration pnyx T12, ADR-0015 — il n'y a plus de résolveur métadonnées côté web.)
 */

/** Lit le code d'erreur métier (`{ error: { code } }`) d'une réponse non-OK. */
async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON.
  }
  return 'UNKNOWN';
}

/**
 * Résultat DISCRIMINÉ d'une résolution publique : la cible existe (`ok`), a expiré ou été
 * supprimée (`gone`, `410`), n'a jamais existé (`notFound`, `404`), ou n'a PAS PU ÊTRE VÉRIFIÉE
 * (`error` — panne réseau, 5xx, corps illisible).
 *
 * ⚠ `error` n'est pas un raffinement cosmétique : ces pages sont les SEULES vues par un visiteur
 * ANONYME, qui n'a ni session ni recours. Replier une panne sur `notFound` lui affirme que son
 * lien est mort — une information FAUSSE, sans bouton pour réessayer, sur la page la plus exposée
 * du produit. Les trois autres verdicts portent une affirmation sur la CIBLE ; `error` dit
 * seulement que le serveur n'a rien affirmé du tout.
 */
export type PublicResolution<T> =
  | { status: 'ok'; data: T }
  | { status: 'gone' }
  | { status: 'notFound' }
  | { status: 'error' };

/**
 * Garantit (idempotent) un lien de partage pour une cible ACTIVE possédée. Lève
 * `StorageApiError` sur réponse non-OK (403 non-writer, 404 cible absente, etc.).
 */
export async function ensureShare(
  targetKind: ShareTargetKind,
  targetId: string,
): Promise<ShareLink> {
  const res = await apiFetch('/api/share', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetKind, targetId }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return shareLinkSchema.parse(await res.json());
}

/**
 * Résolution publique générique : mappe le statut HTTP (+ code métier) en résultat discriminé.
 * Ne REJETTE jamais — tout ce qui empêche d'obtenir un verdict (panne réseau, 5xx, corps
 * illisible/non conforme) devient `error`, et SEULS `404` et `410` parlent de la cible.
 */
async function resolvePublic<T>(
  path: string,
  parse: (value: unknown) => T,
): Promise<PublicResolution<T>> {
  let res: Response;
  try {
    // `fetch` NU (pas `apiFetch`) : page publique déconnectée, aucun cookie/slot/trace.
    res = await fetch(path);
  } catch {
    return { status: 'error' }; // pas de réponse HTTP du tout : le lien n'est pas en cause.
  }
  if (res.ok) {
    try {
      return { status: 'ok', data: parse(await res.json()) };
    } catch {
      return { status: 'error' }; // JSON illisible ou hors contrat : réessayable, pas « mort ».
    }
  }
  const code = await errorCodeOf(res);
  if (res.status === 410 || code === 'GONE') return { status: 'gone' };
  if (res.status === 404) return { status: 'notFound' };
  return { status: 'error' };
}

/** Résout le contenu PUBLIC d'un dossier partagé (`GET /api/share/d/:slug`). */
export function fetchPublicFolder(slug: string): Promise<PublicResolution<PublicFolder>> {
  return resolvePublic(shareFolderPath(slug), (v) => publicFolderSchema.parse(v));
}

/**
 * MINT (idempotent) d'un vrai lien court `/f/<slug>` pour un FICHIER d'un dossier partagé
 * (XFILE-29 ; `POST …/f/:fileId/link`). PUBLIC — `fetch` NU (aucun cookie/slot) : le slug du
 * dossier partagé EST l'autorisation (un visiteur ANONYME peut forger le lien d'un fichier du
 * dossier). Sur réponse non-OK, lève un `StorageApiError` EXPLOITABLE portant le statut + le code
 * métier (`NOT_FOUND` fichier absent du dossier, `GONE` dossier expiré/supprimé) — à charge de
 * l'appelant d'afficher un toast d'erreur.
 */
export async function mintPublicFileLink(slug: string, fileId: string): Promise<ShareLink> {
  const res = await fetch(shareFolderFileLinkPath(slug, fileId), { method: 'POST' });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return shareLinkSchema.parse(await res.json());
}

/**
 * ESTIMATION du volume (`{ count, totalBytes }`) du ZIP d'un dossier partagé (XFILE-29 ;
 * `GET …/archive/estimate`) — annoncée AVANT de lancer le téléchargement. PUBLIC — `fetch` NU.
 *
 * `token` (XFILE-34) est OPTIONNEL et porte une SÉLECTION préparée par
 * {@link preparePublicArchiveSelection} : fourni, l'estimation ne couvre QUE les fichiers cochés ;
 * absent, elle couvre TOUT le dossier (comportement d'origine, requête HTTP identique — aucun
 * `?token=` n'est ajouté). Lève un `StorageApiError` sur réponse non-OK (jeton inconnu / échu).
 */
export async function fetchPublicArchiveEstimate(
  slug: string,
  token?: string,
): Promise<ArchiveEstimate> {
  const res = await fetch(withArchiveToken(shareFolderArchiveEstimatePath(slug), token));
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return archiveEstimateSchema.parse(await res.json());
}

/**
 * PRÉPARE le ZIP d'une SÉLECTION de fichiers d'un dossier partagé (XFILE-34 ;
 * `POST …/archive/prepare`) → JETON opaque à courte durée de vie liant la sélection VALIDÉE au
 * slug. PUBLIC — `fetch` NU (le slug du dossier EST l'autorisation) : le serveur vérifie que
 * CHAQUE id appartient au sous-arbre du dossier partagé (un seul id hors périmètre invalide la
 * sélection entière). Le jeton se passe ensuite à {@link fetchPublicArchiveEstimate} et à
 * {@link publicArchiveUrl}. Lève un `StorageApiError` sur réponse non-OK (400 sélection invalide /
 * vide, 404 id hors du dossier, 410 dossier expiré).
 */
export async function preparePublicArchiveSelection(
  slug: string,
  selection: BulkArchiveRequest,
): Promise<BulkArchivePrepareResult> {
  const res = await fetch(shareFolderArchivePreparePath(slug), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(selection),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return bulkArchivePrepareResultSchema.parse(await res.json());
}

/**
 * URL de TÉLÉCHARGEMENT ZIP d'un dossier partagé, rendue en `<a href download>` cliqué par
 * programme (téléchargement browser-natif ; aucun slot — route publique). Avec `token`, elle porte
 * la SÉLECTION préparée ; sans, TOUT le dossier.
 */
export function publicArchiveUrl(slug: string, token?: string): string {
  return withArchiveToken(shareFolderArchivePath(slug), token);
}

/** Ajoute le `?token=` d'une sélection préparée à un chemin d'archive — sans jeton, chemin intact. */
function withArchiveToken(path: string, token: string | undefined): string {
  return token === undefined ? path : `${path}?token=${encodeURIComponent(token)}`;
}
